import { useEffect, useState, useCallback, useRef } from 'react'
import { api } from '../api/api.tm'
import { initializeCardinal } from '../components/CreditCard/Cardinal'
import {
  setupPaymentValidation,
  triggerCardinalAuthenticationWithVerifone,
} from '../components/CreditCard/Cardinal/3DS'
import {
  LookupInputType,
  Person,
  RecurringPaymentInput,
  SecurePaymentsParesStatus,
  ThreeDAuthenticationInput,
  SecurePaymentLookupResponseType,
  RecurringPaymentResponseType,
} from '../api/types'
import { mapElectronicCommerceIndicator, mapParseStatusEnum } from '../components/CreditCard/Cardinal/utils'
import { ValidationResult } from '../components/CreditCard/Cardinal/types'
import { encryptCard, CreditCardEncryptInfo } from '../components/CreditCard/Cardinal/encryptCard'
import {
  addThreeDSBreadcrumb,
  captureThreeDSFailure,
  captureThreeDSSuccess,
  getCardinalRejectionContext,
  ThreeDSContext,
  ThreeDSOutcome,
  ThreeDSStep,
} from '../components/CreditCard/Cardinal/threeDSecureTelemetry'

const defaultNow = () => Date.now()

interface UseThreeDSecurePaymentReturn {
  isLoadingPayment: boolean
  setIsLoadingPayment: (loading: boolean) => void
  initializePayment: (creditCardInfo: CreditCardEncryptInfo) => Promise<void>
  recurringPaymentData?: RecurringPaymentResponseType
  error?: string
}

export const useThreeDSecurePayment = (
  person: Person | undefined,
  onError: (error?: any, messageType?: 'try again' | 'different method') => void,
  now: () => number = defaultNow
): UseThreeDSecurePaymentReturn => {
  const [isLoadingPayment, setIsLoadingPayment] = useState(false)
  const [encryptedCard, setEncryptedCard] = useState<string | undefined>(undefined)
  const [securePaymentLookup, setSecurePaymentLookup] = useState<{
    data?: SecurePaymentLookupResponseType
    loading?: boolean
    error?: any
  }>({})
  const [recurringPayment, setRecurringPayment] = useState<{
    data?: RecurringPaymentResponseType
    loading?: boolean
    error?: any
  }>({})
  const [threeDResult, setThreeDResult] = useState<ValidationResult | undefined>(undefined)

  // Sentry telemetry state for the current attempt (refs: no re-renders, no effect on the flow)
  const flowActiveRef = useRef(false)
  const flowStartedAtRef = useRef<number | undefined>(undefined)
  const flowContextRef = useRef<ThreeDSContext>({})
  const updateFlowContext = (context: ThreeDSContext) => {
    flowContextRef.current = { ...flowContextRef.current, ...context }
  }

  const handleError = useCallback(
    (
      error?: any,
      messageType?: 'try again' | 'different method',
      step: ThreeDSStep = 'start',
      outcome: Exclude<ThreeDSOutcome, 'success'> = 'error'
    ) => {
      if (flowActiveRef.current) {
        // Only the first failure of an attempt is captured, so one failed purchase = one Sentry event
        flowActiveRef.current = false
        const durationMs = flowStartedAtRef.current === undefined ? undefined : now() - flowStartedAtRef.current
        captureThreeDSFailure(step, error, { ...flowContextRef.current, durationMs }, outcome)
      } else {
        addThreeDSBreadcrumb(step, 'Additional error after the attempt had already failed')
      }
      onError(error, messageType)
      setIsLoadingPayment(false)
    },
    [onError, now]
  )

  // Helper function to perform secure payment lookup
  const performSecurePaymentLookup = async (sessionId: string, encryptedCard: string) => {
    // Split name into first and last name
    const nameParts = person?.name?.split(' ') || []
    const firstName = nameParts.slice(0, -1).join(' ')
    const lastName = nameParts[nameParts.length - 1] || ''

    const lookupData: LookupInputType = {
      amount: 0,
      billingInformation: {
        city: person?.postal?.name || '',
        address: person?.address ?? '',
        countryCode: 'IS',
        emailAddress: person?.email?.personal || '',
        firstName: firstName ?? '',
        lastName: lastName ?? '',
      },
      deviceInfoId: sessionId,
      encryptedCard: encryptedCard,
      merchantReference: 'TM-toyota-web',
    }

    addThreeDSBreadcrumb('lookup', 'Starting 3DS lookup')
    setSecurePaymentLookup({ loading: true })
    try {
      const results = await api.getSecurePaymentLookup({ data: lookupData })
      setSecurePaymentLookup({ data: results.secureCardPaymentLookup, loading: false })
      return results.secureCardPaymentLookup
    } catch (error) {
      setSecurePaymentLookup({ error, loading: false })
      throw error
    }
  }

  // 0. Start loading and encrypt card
  const initializePayment = async (creditCardInfo: CreditCardEncryptInfo) => {
    flowActiveRef.current = true
    flowStartedAtRef.current = now()
    flowContextRef.current = {}
    addThreeDSBreadcrumb('start', '3DS payment flow started')
    // Results/errors from a previous attempt must never be combined with this attempt
    setThreeDResult(undefined)
    setSecurePaymentLookup({})
    setRecurringPayment({})
    setIsLoadingPayment(true)

    // Exchange tokens
    try {
      await api.exchangeToken()
    } catch (error) {
      handleError(error, 'try again', 'token_exchange')
      return
    }

    let encryptedCardInfo: string | undefined
    let cardTokenValue: string
    // Only used to label the Sentry event with the step that failed
    let currentStep: ThreeDSStep = 'card_encryption'

    try {
      // Encrypt card
      encryptedCardInfo = await encryptCard(creditCardInfo)
      setEncryptedCard(encryptedCardInfo!)
      addThreeDSBreadcrumb('card_encryption', 'Card encrypted')

      // Get card token
      currentStep = 'card_token'
      const results = await api.getCardToken()
      if (!results?.viewer?.secureCardPaymentAuthentication?.jsonWebToken) {
        handleError(new Error('Failed to get card token'), 'try again', 'card_token')
        return
      }
      cardTokenValue = results.viewer.secureCardPaymentAuthentication.jsonWebToken
    } catch (error) {
      handleError(error, 'try again', currentStep)
      return
    }

    // 1. Initialize Cardinal 3D Secure
    let sessionIdValue: string
    try {
      addThreeDSBreadcrumb('cardinal_setup', 'Received 3DS JWT, initializing Cardinal')
      sessionIdValue = await initializeCardinal(cardTokenValue)
    } catch (error) {
      handleError(error, 'try again', 'cardinal_setup')
      return
    }

    // 2. Setup Payment Flow - perform lookup and setup validation listener
    try {
      // Start the lookup with the encrypted card
      const lookupResult = await performSecurePaymentLookup(sessionIdValue, encryptedCardInfo!)

      // Setup validation listener for this specific transaction
      setupPaymentValidation()
        .then((result: ValidationResult) => {
          addThreeDSBreadcrumb('challenge_validation', 'Cardinal validation succeeded', {
            actionCode: result.data.ActionCode,
            challengeParesStatus: result.data.Payment.ExtendedData.PAResStatus,
          })
          setThreeDResult(result)
          return result
        })
        .catch((error: unknown) => {
          // A failed/cancelled/timed out challenge must stop the loading state and inform the user.
          // Re-throwing here was an unhandled rejection that left the purchase spinning forever.
          const rejectionContext = getCardinalRejectionContext(error)
          updateFlowContext(rejectionContext)
          // FAILURE / CANCEL = cardholder or issuer outcome, anything else (ERROR, TIMEOUT, ...) = technical
          const isDeclined = rejectionContext.actionCode === 'FAILURE' || rejectionContext.actionCode === 'CANCEL'
          handleError(error, 'try again', 'challenge_validation', isDeclined ? 'declined' : 'error')
        })

      // 3. Trigger Cardinal authentication with the lookup data
      if (lookupResult?.threeDSecureLookup) {
        const data = lookupResult.threeDSecureLookup
        updateFlowContext({
          challengeRequired: data.paresStatus === SecurePaymentsParesStatus.CHALLENGE_REQUIRED_FOR_AUTHENTICATION,
          lookupParesStatus: data.paresStatus,
          enrolledStatus: data.enrolledStatus,
          threeDSecureVersion: data.threeDSecureVersion,
          electronicCommerceIndicator: data.electronicCommerceIndicator ?? undefined,
          transactionId: data.transactionId,
        })
        addThreeDSBreadcrumb('lookup', '3DS lookup completed', flowContextRef.current)
        const lookupResponse = {
          paresStatus: data.paresStatus,
          accessControlServerUrl: data.accessControlServerUrl,
          payload: data.jwtPayload,
          transactionId: data.transactionId,
        }

        try {
          await triggerCardinalAuthenticationWithVerifone(lookupResponse)
        } catch (error) {
          handleError(error, 'different method', 'challenge', 'declined')
          return
        }
      }

      // Validation promise continues in background and will update threeDResult when complete
    } catch (error) {
      handleError(error, 'try again', 'lookup')
    }
  }

  // 4. Effect to create recurring payment once we have both 3DS result and lookup data
  useEffect(() => {
    // Early returns for error cases or missing data
    if (securePaymentLookup?.error) {
      handleError(securePaymentLookup.error, 'try again', 'lookup')
      return
    }

    if (!securePaymentLookup?.data?.threeDSecureLookup || !encryptedCard) {
      return
    }

    // Stop on error too: otherwise loading true -> false re-runs this effect and retries createRecurringPayment endlessly.
    // A new attempt (initializePayment) clears the error.
    if (recurringPayment?.data || recurringPayment?.loading || recurringPayment?.error) {
      return
    }

    const lookupData = securePaymentLookup.data.threeDSecureLookup
    const { paresStatus } = lookupData

    // Handle cases without 3DS challenge result yet
    if (!threeDResult) {
      if (paresStatus === SecurePaymentsParesStatus.CHALLENGE_REQUIRED_FOR_AUTHENTICATION) {
        // 3D Secure challenge is required, wait for user to complete challenge
        return
      }
      if (
        paresStatus !== SecurePaymentsParesStatus.SUCCESSFUL_AUTHENTICATION &&
        paresStatus !== SecurePaymentsParesStatus.SUCCESSFUL_ATTEMPTS_TRANSACTION
      ) {
        handleError(
          `3DS lookup returned non-successful status: ${paresStatus}`,
          'different method',
          'authentication_result',
          'declined'
        )
        return
      }
    }

    // Validate 3DS result if present
    if (threeDResult) {
      const { PAResStatus, SignatureVerification } = threeDResult.data.Payment.ExtendedData
      updateFlowContext({
        actionCode: threeDResult.data.ActionCode,
        challengeParesStatus: PAResStatus,
        signatureVerification: SignatureVerification,
      })
      if (PAResStatus === 'N' || PAResStatus === 'U' || PAResStatus === 'R') {
        handleError(
          new Error(`3D Secure authentication failed with status: ${PAResStatus}`),
          'different method',
          'authentication_result',
          'declined'
        )
        return
      }
    }

    // Construct the payload - prioritize 3DS result data, fall back to lookup data
    const extendedData = threeDResult?.data.Payment.ExtendedData
    const cavv = extendedData?.CAVV ?? lookupData.cavv
    const parsedParesStatus = extendedData?.PAResStatus
      ? mapParseStatusEnum(extendedData.PAResStatus.toUpperCase())
      : paresStatus
    const eci = extendedData?.ECIFlag
      ? mapElectronicCommerceIndicator(extendedData.ECIFlag)
      : lookupData.electronicCommerceIndicator || ''

    const threeDAuth: ThreeDAuthenticationInput | null =
      cavv && parsedParesStatus
        ? {
            cavv,
            directoryServerTransactionId: lookupData.directoryServerTransactionId || '',
            enrolledStatus: lookupData.enrolledStatus || '',
            electronicCommerceIndicator: eci,
            paresStatus: parsedParesStatus,
            threeDSecureVersion: extendedData?.ThreeDSVersion ?? lookupData.threeDSecureVersion ?? '',
          }
        : null

    const paymentData: RecurringPaymentInput = {
      encryptedCard,
      threeDAuthentication: threeDAuth,
    }

    updateFlowContext({ hasThreeDAuthentication: threeDAuth !== null })
    addThreeDSBreadcrumb('recurring_payment', 'Creating recurring payment', flowContextRef.current)

    setRecurringPayment({ loading: true })
    api
      .createRecurringPayment({ data: paymentData })
      .then((results: { createRecurringCardPayment: RecurringPaymentResponseType }) => {
        setRecurringPayment({ data: results.createRecurringCardPayment, loading: false })
      })
      .catch((error: any) => {
        setRecurringPayment({ error, loading: false })
      })
  }, [
    threeDResult,
    securePaymentLookup,
    encryptedCard,
    recurringPayment?.data,
    recurringPayment?.loading,
    recurringPayment?.error,
    handleError,
  ])

  // 5. Handle recurring payment result
  useEffect(() => {
    if (recurringPayment?.data && !recurringPayment.loading) {
      // Recurring payment created successfully, stop loading state
      if (flowActiveRef.current) {
        flowActiveRef.current = false
        const durationMs = flowStartedAtRef.current === undefined ? undefined : now() - flowStartedAtRef.current
        captureThreeDSSuccess({ ...flowContextRef.current, durationMs })
      }
      setIsLoadingPayment(false)
    }
    if (recurringPayment?.error) {
      handleError(recurringPayment.error, 'try again', 'recurring_payment')
    }
  }, [recurringPayment, handleError, now])

  return {
    isLoadingPayment,
    setIsLoadingPayment,
    initializePayment,
    recurringPaymentData: recurringPayment?.data,
    error: recurringPayment?.error || securePaymentLookup?.error,
  }
}
